import {
  getV1CompaniesCompanyIdMaterials,
  MaterialState,
  patchV1CompaniesCompanyIdMaterialsId,
  postV1CompaniesCompanyIdMaterials,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Material } from '~hooks/useMaterials/models';
import { CSVColumn } from '~utils/csv/csvProcessor';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { EntityCSVConfig } from './types';

const materialsColumns: CSVColumn[] = [
  {
    key: 'id',
    header: 'id',
    required: false,
    exampleValue: '',
    description: 'Existing ID (for updates only)',
  },
  {
    key: 'name',
    header: 'name',
    required: true,
    exampleValue: 'Sand',
    description: 'Material name (must be unique)',
  },
  {
    key: 'material_type',
    header: 'material_type',
    required: false,
    exampleValue: 'Aggregate',
    description: 'Material type (must exist in system)',
  },
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'MAT-001',
    description: 'Your internal reference ID',
  },
  {
    key: 'status',
    header: 'status',
    required: false,
    enumValues: ['ACTIVE', 'INACTIVE'],
    exampleValue: 'ACTIVE',
    description: 'ACTIVE or INACTIVE',
  },
];

export const createMaterialsConfig = (
  getMaterialTypeIdByName: (name: string) => string | undefined,
  scopedCompanyId: string,
): EntityCSVConfig<Material> => ({
  entityType: 'materials',
  columns: materialsColumns,

  fetchAll: (companyId: string) =>
    fetchAllApiPages(
      (query) =>
        getV1CompaniesCompanyIdMaterials({
          path: { 'company-id': companyId },
          query,
        }),
      Material.parse,
    ),

  create: async (companyId: string, data: Record<string, string>): Promise<Material> => {
    const materialTypeId = data.material_type
      ? getMaterialTypeIdByName(data.material_type)
      : undefined;

    const response = await postV1CompaniesCompanyIdMaterials({
      path: { 'company-id': companyId },
      body: {
        name: data.name,
        external_id: data.external_id || undefined,
        state:
          data.status?.toUpperCase() === 'INACTIVE'
            ? MaterialState.INACTIVE
            : MaterialState.ACTIVE,
        material_type_id: materialTypeId,
      },
    });

    return Material.parse(response.data.data);
  },

  update: async (id: string, data: Record<string, string>): Promise<Material> => {
    const materialTypeId = data.material_type
      ? getMaterialTypeIdByName(data.material_type)
      : undefined;

    const response = await patchV1CompaniesCompanyIdMaterialsId({
      path: { 'company-id': scopedCompanyId, id },
      body: {
        name: data.name,
        external_id: data.external_id || undefined,
        state:
          data.status?.toUpperCase() === 'INACTIVE'
            ? MaterialState.INACTIVE
            : MaterialState.ACTIVE,
        material_type_id: materialTypeId,
      },
    });

    return Material.parse(response.data.data);
  },

  transformExport: (material: Material): Record<string, string> => ({
    id: material.id,
    name: material.name,
    material_type: material.typeName || '',
    external_id: material.externalId || '',
    status: material.isActive ? 'ACTIVE' : 'INACTIVE',
  }),

  transformImport: (row: Record<string, string>): Record<string, unknown> => ({
    name: row.name?.trim(),
    external_id: row.external_id?.trim() || null,
    material_type: row.material_type?.trim() || null,
    state:
      row.status?.toUpperCase() === 'INACTIVE'
        ? MaterialState.INACTIVE
        : MaterialState.ACTIVE,
  }),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];
    if (!row.name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'name' }));
    }
    if (row.status && !['ACTIVE', 'INACTIVE'].includes(row.status.toUpperCase())) {
      errors.push(
        t('csv_import_export.errors.invalid_enum', {
          field: 'status',
          values: 'ACTIVE, INACTIVE',
        }),
      );
    }
    return errors;
  },
});
